// Service autonome de la case Operation x BCI (acheteur, paniers importes).
// Cible les vraies routes acheteur /bdci/operation/* (core api.php l.1054-1099) + /annuler
// (utilise par le V1 via entityService, donc existant cote core).
// Superset des memes noms d'export que SuiviOperationBCV2/services/operationBcService.ts, pour
// que les feuilles clonees compilent ; les differences BCI sont portees ICI :
//   - refuserOffre est au niveau OPERATION (POST /bdci/operation/{id}/refuse), sans id_commande ;
//   - fetchSignatureModeBdc renvoie 'automatique' (BCI n'a pas de signature manuscrite/OTP) ->
//     le flux SignatureFlow des feuilles ne se declenche jamais ;
//   - lancerConsultation(opId, body) PUT la consultation avec le payload (mapping V1).

import { Signataire } from '../types';

const authedFetch = async <T = unknown>(path: string, options: RequestInit = {}): Promise<T> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("Aucun token d'authentification trouvé");

  const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || `Erreur HTTP: ${response.status}`);
  }
  return response.json().catch(() => ({})) as Promise<T>;
};

export interface ReferenceUpdateResponse {
  data?: unknown;
}
export interface NotifierResponse {
  responsable?: { prenom?: string; nom?: string };
}

// Acheteur accepte l'offre de la commande : POST /bdci/operation/{id}/accept/{commandeId}
export const accepterOffre = (operationId: number, commandeId: number): Promise<unknown> =>
  authedFetch(`/bdci/operation/${operationId}/accept/${commandeId}`, { method: 'POST' });

// Acheteur refuse l'offre (niveau operation en BCI, l'id_commande est ignore) :
// POST /bdci/operation/{id}/refuse.
export const refuserOffre = (operationId: number, _commandeId?: number): Promise<unknown> =>
  authedFetch(`/bdci/operation/${operationId}/refuse`, { method: 'POST' });

// Transfere l'operation au prestataire : POST /bdci/operation/{id}/transfertPrestataire
export const transfertPrestataire = (operationId: number): Promise<{ data?: unknown }> =>
  authedFetch<{ data?: unknown }>(`/bdci/operation/${operationId}/transfertPrestataire`, {
    method: 'POST',
  });

// Annule l'operation : POST /bdci/operation/{id}/annuler (route utilisee par le V1).
export const annulerOperation = (operationId: number): Promise<unknown> =>
  authedFetch(`/bdci/operation/${operationId}/annuler`, { method: 'POST' });

// Met a jour le numero de reference interne : PUT /bdci/operation/{id}
export const updateReferenceInterne = (
  operationId: number,
  reference: string,
): Promise<ReferenceUpdateResponse> =>
  authedFetch<ReferenceUpdateResponse>(`/bdci/operation/${operationId}`, {
    method: 'PUT',
    body: JSON.stringify({ reference }),
  });

// Liste des acheteurs signataires (validateurs) : GET /acheteurs/signataires
export const loadSignataires = async (): Promise<Signataire[]> => {
  const result = await authedFetch<{ data?: Signataire[]; signataires?: Signataire[] }>(
    '/acheteurs/signataires',
  );
  return result.data || result.signataires || [];
};

// Notifie un responsable signataire : POST /bdci/operation/{id}/notification-responsable
export const notifierResponsable = (
  operationId: number,
  responsableId: number,
  responsableType: string,
): Promise<NotifierResponse> =>
  authedFetch<NotifierResponse>(`/bdci/operation/${operationId}/notification-responsable`, {
    method: 'POST',
    body: JSON.stringify({ responsable_id: responsableId, responsable_type: responsableType }),
  });

// Telecharge le bon de commande (reponse PDF brute) : POST /documents/signed-contrat, source BCI.
// (a confirmer : existence d'un document signe cote BCI ; sinon le bouton de telechargement
// n'apparait qu'au statut Valide.)
export const telechargerBonCommande = async (externalId: string | number): Promise<Blob> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("Aucun token d'authentification trouvé");

  const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/signed-contrat`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
    },
    body: JSON.stringify({ external_id: String(externalId), external_type: 'operation', source: 'BCI' }),
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || 'Erreur lors du telechargement du bon de commande');
  }
  return response.blob();
};

// BCI n'a pas de signature manuscrite/OTP : on force le mode 'automatique' (acceptation directe
// via popup de confirmation), ce qui neutralise le flux SignatureFlow des feuilles.
export const fetchSignatureModeBdc = async (): Promise<'automatique' | 'manuscrite' | null> =>
  'automatique';

// --- Onglet Consultation ------------------------------------------------------

export class ConsultationValidationError extends Error {
  validationErrors: Record<string, string[]>;

  constructor(validationErrors: Record<string, string[]>) {
    super('Erreurs de validation détectées');
    this.name = 'ConsultationValidationError';
    this.validationErrors = validationErrors;
  }
}

export interface SaveDescriptifResponse {
  status?: string;
  message?: string;
  data?: unknown;
}

// Sauvegarde le descriptif : PUT /bdci/operation/{id}. Remonte les 422 par champ.
export const saveOperationDescriptif = async (
  operationId: number | string,
  body: Record<string, unknown>,
): Promise<SaveDescriptifResponse> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("Aucun token d'authentification trouvé");

  const response = await fetch(`${process.env.REACT_APP_API_URL}/bdci/operation/${operationId}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as {
      message?: string;
      errors?: Record<string, string[]>;
    };
    if (response.status === 422 && errorData.errors) {
      throw new ConsultationValidationError(errorData.errors);
    }
    throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
  }

  const data = (await response.json()) as SaveDescriptifResponse;
  if (data.status !== 'success') {
    throw new Error(data.message || 'Erreur lors de la sauvegarde');
  }
  return data;
};

// Met a jour la quantite d'un article : PUT /bdci/operation/{id}/catalogue/{catalogueId}
export const updateArticleQuantity = (
  operationId: number,
  catalogueId: string | number,
  quantity: number,
): Promise<unknown> =>
  authedFetch(`/bdci/operation/${operationId}/catalogue/${catalogueId}`, {
    method: 'PUT',
    body: JSON.stringify({ quantity: String(quantity) }),
  });

// Supprime un article : DELETE /bdci/operation/{id}/catalogue/{catalogueId}
export const removeArticle = (
  operationId: number,
  catalogueId: string | number,
): Promise<unknown> =>
  authedFetch(`/bdci/operation/${operationId}/catalogue/${catalogueId}`, { method: 'DELETE' });

// Lance la consultation avec le payload mappe (cf. domain operationConsultation.buildConsultationLaunchBody).
export const lancerConsultation = (
  operationId: number,
  body: Record<string, unknown>,
): Promise<unknown> =>
  authedFetch(`/bdci/operation/${operationId}/consultation`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

// Variante sans corps (chemin "rang suivant" du gabarit BC). En BCI (1 commande, pas de cascade)
// ce chemin est inerte ; conservee pour la compatibilite d'import de la feuille clonee.
export const lancerConsultationRang = (operationId: number): Promise<unknown> =>
  authedFetch(`/bdci/operation/${operationId}/consultation`, { method: 'PUT' });
